import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import {
  VITE_API_CLIENT_ID,
  VITE_API_CLIENT_SECRET,
  VITE_API_URL,
} from "../constants/env";
import { API_ENDPOINTS } from "../constants/endpoints";

interface ApiError {
  message: string;
  status?: number;
  code?: string;
  errors?: string[];
}

class ApiService {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{ resolve: Function; reject: Function }> = [];

  constructor() {
    this.instance = axios.create({
      baseURL: VITE_API_URL,
      timeout: 10000,
      headers: {
        common: {
          "Content-Type": "application/json",
        },
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor - Add bearer token to header
    this.instance.interceptors.request.use(
      (config) => {
        try {
          // Ensure Content-Type is set for requests with data
          if (
            (config.method === "post" ||
              config.method === "put" ||
              config.method === "patch") &&
            config.data
          ) {
            config.headers["Content-Type"] =
              config.headers["Content-Type"] || "application/json";
          }

          const token = localStorage.getItem("authToken");
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch (error) {
          console.warn("Failed to retrieve auth token:", error);
        }
        return config;
      },
      (error) => {
        console.error("Request interceptor error:", error);
        return Promise.reject(this.normalizeError(error));
      },
    );

    // Response interceptor - Log api errors and handle common scenarios
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        // Handle 401 Unauthorized - attempt token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, queue this request
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then(() => {
                return this.instance(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem("refreshToken");
            if (!refreshToken) {
              throw new Error("No refresh token available");
            }

            const refreshResponse = await this.refreshToken(refreshToken);

            // Update stored tokens
            localStorage.setItem("authToken", refreshResponse.access_token);
            localStorage.setItem("refreshToken", refreshResponse.refresh_token);

            // Update authorization header for the original request
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${refreshResponse.access_token}`;
            }

            // Process queued requests
            this.processQueue(null);

            // Retry original request
            return this.instance(originalRequest);
          } catch (refreshError) {
            // Refresh failed, clear tokens and redirect to login
            this.processQueue(refreshError);
            localStorage.removeItem("authToken");
            localStorage.removeItem("refreshToken");

            if (typeof window !== "undefined") {
              window.location.href = "/login";
            }

            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        const normalizedError = this.normalizeError(error);

        console.error("API Error:", {
          status: error.response?.status,
          message: normalizedError.message,
          url: error.config?.url,
          method: error.config?.method,
        });

        return Promise.reject(normalizedError);
      },
    );
  }

  private async refreshToken(
    refreshToken: string,
  ): Promise<{ access_token: string; refresh_token: string }> {
    const response = await axios.post(
      `${VITE_API_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
      {
        refresh_token: refreshToken,
        grant_type: "refresh_token",
        client_id: VITE_API_CLIENT_ID,
        client_secret: VITE_API_CLIENT_SECRET,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  }

  private processQueue(error: any) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });

    this.failedQueue = [];
  }

  private normalizeError(error: any): ApiError {
    if (error.response) {
      // Server responded with error status
      // Prioritize human-readable messages: error_description > message > error > HTTP status
      return {
        message:
          error.response.data?.error_description ||
          error.response.data?.message ||
          error.response.data?.error ||
          `HTTP ${error.response.status}`,
        status: error.response.status,
        code: error.response.data?.code || error.response.data?.error,
        errors: error.response.data?.errors,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: "Network error - please check your connection",
        code: "NETWORK_ERROR",
      };
    } else {
      // Something else happened
      return {
        message: error.message || "An unexpected error occurred",
        code: "UNKNOWN_ERROR",
        errors: error.errors,
      };
    }
  }

  public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.instance.get(url, config);
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  public async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.instance.post(
        url,
        data,
        config,
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  public async put<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.instance.put(
        url,
        data,
        config,
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }
  public async patch<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    try {
      const patchConfig: AxiosRequestConfig = {
        ...config,
        headers: {
          ...config?.headers,
          "Content-Type": "application/json",
        },
      };
      // Ensure Content-Type is set for PATCH requests
      const response: AxiosResponse<T> = await this.instance.patch(
        url,
        data,
        patchConfig,
      );
      //console.log(response);
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.instance.delete(
        url,
        config,
      );
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }

  public async getBlob(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(url, {
        ...config,
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      throw this.normalizeError(error);
    }
  }
}

export const apiService = new ApiService();
